/* ==========================================================================
   RAFALE — Moteur d'animations au scroll
   - IntersectionObserver, requestAnimationFrame, aucune dépendance.
   - Attributs : data-rafale-animation, data-rafale-delay (ms), data-rafale-duration (ms),
     data-rafale-distance (px), data-rafale-repeat ("true" = rejoue à chaque apparition),
     data-rafale-stagger (ms entre enfants), data-rafale-split ("words" | "lines"),
     data-rafale-counter (valeur finale), data-rafale-counter-duration, data-rafale-locale,
     data-rafale-power (0-100), data-rafale-parallax (intensité 0.05-0.4), data-rafale-float.
   - Encapsulé : n'expose que window.RafaleAnimations.init(root) et .destroy(root).
   ========================================================================== */
(function () {
  'use strict';
  if (window.RafaleAnimations) return;

  var docEl = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 749px)');
  var initialized = new WeakSet();
  var willChangeTimers = new WeakMap();
  var observer = null;
  var parallaxItems = [];
  var parallaxTicking = false;
  var parallaxBound = false;

  function num(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  /* ---------- Préparation d'un élément ---------- */
  function prepare(el) {
    if (!el || initialized.has(el)) return;
    initialized.add(el);

    if (el.hasAttribute('data-rafale-delay')) el.style.setProperty('--rafale-anim-delay', num(el.getAttribute('data-rafale-delay'), 0) + 'ms');
    if (el.hasAttribute('data-rafale-duration')) el.style.setProperty('--rafale-anim-duration', num(el.getAttribute('data-rafale-duration'), 700) + 'ms');
    if (el.hasAttribute('data-rafale-distance')) el.style.setProperty('--rafale-anim-distance', num(el.getAttribute('data-rafale-distance'), 28) + 'px');
    if (el.hasAttribute('data-rafale-power')) {
      var level = Math.max(0, Math.min(100, num(el.getAttribute('data-rafale-power'), 100))) / 100;
      el.style.setProperty('--rafale-power-level', String(level));
    }

    var stagger = el.getAttribute('data-rafale-stagger');
    if (stagger !== null) {
      var step = num(stagger, 120);
      var baseDelay = num(el.getAttribute('data-rafale-delay'), 0);
      Array.prototype.forEach.call(el.children, function (child, i) {
        if (!child.hasAttribute('data-rafale-animation')) child.setAttribute('data-rafale-animation', el.getAttribute('data-rafale-child-animation') || 'fade-up');
        child.style.setProperty('--rafale-anim-delay', (baseDelay + step * i) + 'ms');
        if (el.hasAttribute('data-rafale-repeat')) child.setAttribute('data-rafale-repeat', el.getAttribute('data-rafale-repeat'));
        prepare(child);
      });
    }

    var split = el.getAttribute('data-rafale-split');
    if (split && !el.querySelector('.rafale-split__unit')) splitText(el, split);

    if (el.hasAttribute('data-rafale-parallax')) registerParallax(el);

    if (reduceMotion.matches) {
      reveal(el);
      return;
    }
    if (observer) observer.observe(el);
    else reveal(el);
  }

  /* ---------- Découpage des titres (le texte reste dans le HTML) ---------- */
  function splitText(el, mode) {
    var text = el.textContent;
    if (!text || el.children.length > 0) return; // ne découpe que du texte simple
    var units = mode === 'lines' ? text.split(/\n|\|/) : text.split(/(\s+)/);
    var frag = document.createDocumentFragment();
    var step = num(el.getAttribute('data-rafale-stagger'), mode === 'lines' ? 140 : 45);
    var base = num(el.getAttribute('data-rafale-delay'), 0);
    var index = 0;
    units.forEach(function (unit) {
      if (unit === '') return;
      if (/^\s+$/.test(unit)) { frag.appendChild(document.createTextNode(unit)); return; }
      var span = document.createElement('span');
      span.className = 'rafale-split__unit';
      span.textContent = unit;
      span.style.setProperty('--rafale-anim-delay', (base + step * index) + 'ms');
      frag.appendChild(span);
      index++;
    });
    el.setAttribute('aria-label', text.replace(/\|/g, ' ').replace(/\s+/g, ' ').trim());
    el.textContent = '';
    el.appendChild(frag);
    el.classList.add('rafale-split');
  }

  /* ---------- Révélation ---------- */
  function reveal(el) {
    if (el.classList.contains('rafale-is-visible')) return;
    setWillChange(el);
    el.classList.add('rafale-is-visible');
    if (el.hasAttribute('data-rafale-counter')) runCounter(el);
  }

  function hide(el) {
    el.classList.remove('rafale-is-visible');
    if (el.hasAttribute('data-rafale-counter')) el.removeAttribute('data-rafale-counter-done');
  }

  function setWillChange(el) {
    if (reduceMotion.matches) return;
    el.style.willChange = 'opacity, transform';
    var total = num(getComputedStyle(el).getPropertyValue('--rafale-anim-duration'), 700) + num(getComputedStyle(el).getPropertyValue('--rafale-anim-delay'), 0) + 100;
    var prev = willChangeTimers.get(el);
    if (prev) clearTimeout(prev);
    willChangeTimers.set(el, setTimeout(function () {
      el.style.willChange = '';
      willChangeTimers.delete(el);
    }, total));
  }

  /* ---------- Compteur ---------- */
  function formatNumber(value, locale) {
    try { return new Intl.NumberFormat(locale || 'fr-CA').format(Math.round(value)); }
    catch (e) { return String(Math.round(value)); }
  }

  function runCounter(el) {
    if (el.getAttribute('data-rafale-counter-done') === 'true') return;
    el.setAttribute('data-rafale-counter-done', 'true');
    var target = num(el.getAttribute('data-rafale-counter'), 0);
    var locale = el.getAttribute('data-rafale-locale') || (docEl.lang || 'fr-CA');
    var prefix = el.getAttribute('data-rafale-prefix') || '';
    var suffix = el.getAttribute('data-rafale-suffix') || '';
    var write = function (v) { el.textContent = prefix + formatNumber(v, locale) + suffix; };
    if (reduceMotion.matches) { write(target); return; }
    var duration = num(el.getAttribute('data-rafale-counter-duration'), 1800);
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      write(target * eased);
      if (p < 1) requestAnimationFrame(frame);
      else write(target);
    }
    write(0);
    requestAnimationFrame(frame);
  }

  /* ---------- Parallaxe (rAF, pause hors écran, désactivée sur mobile) ---------- */
  function registerParallax(el) {
    if (mobileQuery.matches || reduceMotion.matches) return;
    if (parallaxItems.some(function (it) { return it.el === el; })) return;
    var item = { el: el, strength: Math.max(0.02, Math.min(0.5, num(el.getAttribute('data-rafale-parallax'), 0.15))), visible: false };
    parallaxItems.push(item);
    if (observer) observer.observe(el);
    if (!parallaxBound) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      parallaxBound = true;
    }
    onScroll();
  }

  function onScroll() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(updateParallax);
  }

  function updateParallax() {
    parallaxTicking = false;
    var vh = window.innerHeight || 800;
    for (var i = 0; i < parallaxItems.length; i++) {
      var it = parallaxItems[i];
      if (!it.visible || !it.el.isConnected) continue;
      var rect = it.el.getBoundingClientRect();
      var center = rect.top + rect.height / 2 - vh / 2;
      var offset = -center * it.strength;
      it.el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
    }
  }

  /* ---------- Observateur ---------- */
  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var el = entry.target;
      var pItem = null;
      for (var j = 0; j < parallaxItems.length; j++) if (parallaxItems[j].el === el) pItem = parallaxItems[j];
      if (pItem) { pItem.visible = entry.isIntersecting; if (entry.isIntersecting) onScroll(); }
      if (!el.hasAttribute('data-rafale-animation') && !el.hasAttribute('data-rafale-counter') && !el.hasAttribute('data-rafale-stagger')) continue;
      if (entry.isIntersecting) {
        reveal(el);
        if (el.getAttribute('data-rafale-repeat') !== 'true' && !pItem) observer.unobserve(el);
      } else if (el.getAttribute('data-rafale-repeat') === 'true') {
        hide(el);
      }
    }
  }

  function createObserver() {
    if (!('IntersectionObserver' in window)) return null;
    return new IntersectionObserver(onIntersect, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
  }

  /* ---------- API ---------- */
  function init(root) {
    root = root || document;
    if (!root.querySelectorAll) return;
    var selector = '[data-rafale-animation], [data-rafale-counter], [data-rafale-stagger], [data-rafale-parallax], [data-rafale-split]';
    var nodes = root.querySelectorAll(selector);
    if (root !== document && root.matches && root.matches(selector)) prepare(root);
    for (var i = 0; i < nodes.length; i++) prepare(nodes[i]);
  }

  function destroy(root) {
    root = root || document;
    var nodes = root.querySelectorAll('[data-rafale-animation], [data-rafale-counter], [data-rafale-parallax]');
    for (var i = 0; i < nodes.length; i++) {
      if (observer) observer.unobserve(nodes[i]);
      initialized.delete(nodes[i]);
    }
    parallaxItems = root === document ? [] : parallaxItems.filter(function (it) { return it.el.isConnected && !root.contains(it.el); });
  }

  function boot() {
    observer = createObserver();
    docEl.classList.add('rafale-anim-ready');
    init(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Éditeur de thème Shopify */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroy(e.target); });
  document.addEventListener('shopify:block:select', function (e) { if (e.target) init(e.target); });

  if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', function () { if (reduceMotion.matches) init(document); });

  window.RafaleAnimations = { init: init, destroy: destroy };
})();
